import { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { useSelector } from "react-redux";
import { useAppContext } from "../../providers/ProviderAppContainer";
import TabHeader from "../common/TabHeader";
import FileInput from "../common/FileInput";
import UserSearch from "../common/UserSearch";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES, TASK_PRIORITIES } from "../../constants";
import { TEXT_NORMAL } from "../../style";
import { getWriteableDate } from "../../utils";

const EMPTY_USER_TASK_STATUSES = [];

export default function DialogAddTask({ visible, closeDialog, onCreated }) {
    const { requestAPI, showToast } = useAppContext();
    const statuses = useSelector((state) => state.stateTemplateConfig?.global?.user_task_statuses ?? EMPTY_USER_TASK_STATUSES);

    const [task, setTask] = useState({});
    const [loading, setLoading] = useState();
    const [assignee, setAssignee] = useState(null);

    useEffect(() => {
        if (!visible) return;
        setTask({ status_id: statuses?.[0]?.id, priority: 0, deadline: null });
        setAssignee(null);
        // Reset only when dialog opens — statuses read at open time via stable selector
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const addTask = useCallback(() => {
        requestAPI({
            requestPath: "user-tasks",
            requestMethod: "POST",
            requestPostBody: {
                title: task.title,
                description: task.description || null,
                user_id: assignee?.id,
                status_id: task.status_id,
                priority: task.priority,
                attachment: task.attachment || null,
                deadline: task.deadline ? getWriteableDate({ date: task.deadline }) : null,
            },
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Task", life: 2000 }),
            onResponseReceieved: (created, responseCode) => {
                if (created && responseCode === 201) {
                    showToast({ severity: "success", summary: "Added", detail: "Task Added", life: 1000 });
                    onCreated?.(created);
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: created?.error || "Failed To Add Task", life: 2000 });
                }
            },
        });
    }, [assignee, closeDialog, onCreated, requestAPI, showToast, task]);

    return (
        <Dialog
            header="Add Task"
            visible={visible}
            className="w-11"
            onHide={closeDialog}
            pt={{
                content: { className: "overflow-y-scroll" },
            }}
        >
            <TabHeader className="pt-3" title="Add Task" highlights={["Assign a task to a user"]} />

            <FloatLabel className="mt-5">
                <InputText
                    className="w-full"
                    value={task?.title || ""}
                    onChange={(e) => setTask((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label>Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    className="w-full"
                    rows={4}
                    value={task?.description || ""}
                    onChange={(e) => setTask((prev) => ({ ...prev, description: e.target.value || null }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label>Description</label>
            </FloatLabel>

            <div className="mt-6">
                <UserSearch
                    className="w-full"
                    value={assignee}
                    onChange={setAssignee}
                    disabled={loading}
                    floatLabel
                    label="Assignee"
                    inputId="add-task-assignee"
                />
            </div>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={statuses.find((s) => Number(s.id) === Number(task?.status_id)) || null}
                    options={statuses}
                    optionLabel="title"
                    placeholder={statuses.length ? "Select status" : "No statuses available"}
                    onChange={(e) => setTask((prev) => ({ ...prev, status_id: e.value?.id }))}
                    disabled={loading || !statuses.length}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label>Status</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={TASK_PRIORITIES.find((p) => Number(p.id) === Number(task?.priority)) || null}
                    options={TASK_PRIORITIES}
                    optionLabel="label"
                    placeholder="Select priority"
                    onChange={(e) => setTask((prev) => ({ ...prev, priority: e.value?.id }))}
                    disabled={loading}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label>Priority</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    className="w-full"
                    value={task?.deadline || null}
                    onChange={(e) => setTask((prev) => ({ ...prev, deadline: e.value || null }))}
                    disabled={loading}
                    showTime
                    hourFormat="24"
                    showIcon
                    showButtonBar
                    dateFormat="dd/mm/yy"
                />
                <label>Deadline</label>
            </FloatLabel>

            <div className="mt-5">
                <FileInput
                    label="Attachment"
                    type="image"
                    cdn_url={task?.attachment}
                    setCDNUrl={(attachment) => setTask((prev) => ({ ...prev, attachment }))}
                    disabled={loading}
                />
            </div>

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_USER_TASK}>
                <Button
                    className="mt-3"
                    label="Add Task"
                    severity="warning"
                    loading={loading}
                    disabled={!task?.title || !assignee?.id || !task?.status_id || task?.priority == null}
                    onClick={addTask}
                />
            </HasRequiredAuthority>
        </Dialog>
    );
}
